import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { PlusOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import ProductSelect from '@components/shared/products/ProductSelect';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import SelectNotFoundContent from '@components/shared/ui/SelectNotFoundContent';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Form,
  Input,
  Button,
  InputNumber,
  Select,
  DatePicker,
  Checkbox,
  Descriptions,
  Divider,
  Space,
  Table,
  Spin,
  message,
  Tooltip,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

const { TextArea } = Input;

const CURRENCY_OPTIONS = [
  { label: 'TWD', value: 'TWD' },
  { label: 'USD', value: 'USD' },
  { label: 'JPY', value: 'JPY' },
  { label: 'EUR', value: 'EUR' },
  { label: 'SGD', value: 'SGD' },
  { label: 'KRW', value: 'KRW' },
  { label: 'CNY', value: 'CNY' },
  { label: 'HKD', value: 'HKD' },
];

interface SupplierOption {
  label: string;
  value: string;
  default_currency?: string | null;
  tax_rate?: number | null;
}

interface ProductOption {
  value: string;
  label: string;
  sku?: string;
  name?: string;
  [key: string]: unknown;
}

interface PurchaseOrderLoadedPayload {
  merchant_id?: string | null;
  merchant?: { id?: string | null } | null;
  [key: string]: unknown;
}

interface PurchaseOrderEditProps {
  // service / supplierService / productService 都是 .js/.ts service 物件，
  // runtime shape 多變（admin/operator 兩套），鏡像 InboundEdit 用 any 放行。
  service: any;
  supplierService: any;
  productService: any;
  role?: 'admin' | 'operator';
  // operator 編輯模式由 wrapper 透過 useRecordMerchantSync 在 PO 載入後呼叫，
  // 把 OperatorDataContext.currentMerchant 對齊到 PO 所屬 merchant，避免下拉誤抓另一商家的清單。
  onPurchaseOrderLoaded?: (po: PurchaseOrderLoadedPayload) => void;
  // True while the wrapper is still switching context to the record's merchant.
  // Render-gated to avoid the form briefly showing the previous merchant's options.
  merchantDataLoading?: boolean;
}

/**
 * 通用 PurchaseOrder 編輯視圖組件
 */
const PurchaseOrderEdit: React.FC<PurchaseOrderEditProps> = ({
  service,
  supplierService,
  productService,
  role = 'admin',
  onPurchaseOrderLoaded,
  merchantDataLoading = false,
}) => {
  const { t } = useTranslation();
  const goBack = useSmartBack();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isNew = !id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string; default_currency?: string | null; tax_rate?: number | null }>>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const addItemRef = useRef<((value: Record<string, unknown>) => void) | null>(null);
  // 編輯模式：載入的 PO 帶有 merchant_id，傳給 ProductSelect 鎖死搜尋範圍，
  // 避免 header 切到別的 merchant 後搜尋帶回別商家的商品。
  const [recordMerchantId, setRecordMerchantId] = useState<string | null>(null);

  // 將新建模式預設值抽成 callback，以便 resetFields 後重新套用
  const applyNewModeDefaults = useCallback(() => {
    form.setFieldsValue({
      purchase_order_items_attributes: [],
      purchase_order_adjustments_attributes: [],
      tax_rate: 0,
      tax_included: false,
    });
  }, [form]);

  const handleAddItem = useCallback(() => {
    if (!addItemRef.current) return;
    addItemRef.current({ key: Date.now(), quantity: 1, unit_price: 0 });
    setTimeout(() => {
      const cards = document.querySelectorAll('.po-item-card');
      if (cards.length > 0) {
        cards[cards.length - 1].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }, []);

  // 載入供應商列表
  const loadSuppliers = useCallback(async () => {
    try {
      setSuppliersLoading(true);
      const res = await supplierService.getRecords({ page: 1, page_size: 100 });
      if (res.success && res.data) {
        setSuppliers(res.data);
      }
    } catch (error) {
      console.error('Failed to load suppliers:', error);
    } finally {
      setSuppliersLoading(false);
    }
  }, [supplierService]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const supplierOptions = useMemo<SupplierOption[]>(() =>
    suppliers.map(s => ({
      label: s.name,
      value: s.id,
      default_currency: s.default_currency,
      tax_rate: s.tax_rate,
    })),
    [suppliers]
  );

  // 供應商變更時自動填入幣別和稅率
  const handleSupplierChange = useCallback((supplierId: string) => {
    const supplier = supplierOptions.find(s => s.value === supplierId);
    if (supplier) {
      const updates: Record<string, unknown> = {};
      if (supplier.default_currency) {
        updates.currency = supplier.default_currency;
      }
      if (supplier.tax_rate != null) {
        updates.tax_rate = supplier.tax_rate;
      }
      if (Object.keys(updates).length > 0) {
        form.setFieldsValue(updates);
      }
    }
  }, [supplierOptions, form]);

  // 建議單號
  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await service.suggestName();
      if (res.success) {
        form.setFieldsValue({ name: res.data.name });
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setSuggesting(false);
    }
  };

  // 載入資料
  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setDataLoading(true);
      if (id) {
        const response = await service.get(id);
        if (response.success) {
          const po = response.data;

          // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
          // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
          // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
          onPurchaseOrderLoaded?.(po);
          // 鎖定 ProductSelect 搜尋的 merchant 範圍：以 PO 自帶的 merchant_id 為準，
          // 不依賴 header 的 currentMerchant（即使 sync 還沒完成也不會找錯商家）。
          if (po.merchant_id) {
            setRecordMerchantId(po.merchant_id);
          }

          // 只有草稿狀態可以編輯
          if (!po.editable) {
            message.warning(t('shared.purchase_orders.form.not_editable'));
            navigate(`/purchase_orders/${id}`, { replace: true });
            return;
          }

          // 處理 items
          const items = (po.items || []).map((item: Record<string, any>) => ({
            ...item,
            key: item.id || Date.now() + Math.random(),
            product_id: item.product_id,
          }));

          // 建構已有商品的 options，讓 ProductSelect 能顯示 sku/name
          const existingProducts: ProductOption[] = (po.items || [])
            .filter((item: Record<string, any>) => item.product_id)
            .map((item: Record<string, any>) => ({
              value: item.product_id,
              label: `${item.product_sku} - ${item.product_name}`,
              sku: item.product_sku,
              name: item.product_name,
            }));
          setProductOptions(existingProducts);

          const adjustments = (po.adjustments || []).map((adj: Record<string, any>) => ({
            ...adj,
            key: adj.id || Date.now() + Math.random(),
          }));

          form.setFieldsValue({
            name: po.name,
            supplier_id: po.supplier_id,
            currency: po.currency,
            tax_rate: po.tax_rate,
            tax_included: po.tax_included,
            expected_delivery_date: po.expected_delivery_date ? dayjs(po.expected_delivery_date) : null,
            note: po.note,
            purchase_order_items_attributes: items,
            purchase_order_adjustments_attributes: adjustments,
          });
        } else {
          handleApiError(response, { t, fallbackMessage: t('common.error.record_not_found') });
          goBack();
        }
      } else {
        // 新增模式
        applyNewModeDefaults();
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load data error:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // 組合送出 payload，給兩個提交按鈕共用
  const buildPayload = (values: Record<string, any>) => {
    const items = (values.purchase_order_items_attributes || []).map((item: Record<string, any>) => ({
      id: item.id,
      product_id: item.product_id,
      quantity: item.quantity,
      unit_price: item.unit_price,
      note: item.note,
      _destroy: item._destroy,
    }));

    const adjustments = (values.purchase_order_adjustments_attributes || []).map((adj: Record<string, any>) => ({
      id: adj.id,
      description: adj.description,
      amount: adj.amount,
      _destroy: adj._destroy,
    }));

    return {
      name: values.name,
      supplier_id: values.supplier_id,
      currency: values.currency,
      tax_rate: values.tax_rate,
      tax_included: values.tax_included || false,
      expected_delivery_date: values.expected_delivery_date
        ? (values.expected_delivery_date as Dayjs).format('YYYY-MM-DD')
        : null,
      note: values.note,
      purchase_order_items_attributes: items,
      purchase_order_adjustments_attributes: adjustments,
    };
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values: Record<string, any>) => {
    const payload = buildPayload(values);
    return isNew ? await service.create(payload) : await service.update(id, payload);
  };

  // 將 response.errors 套回欄位（保留既有 field-level 錯誤處理邏輯）
  const applyFieldErrors = (response: { errors?: Record<string, unknown> }) => {
    if (response.errors) {
      const fieldEntries = Object.entries(response.errors);
      form.setFields(fieldEntries.map(([name, errors]) => ({
        name,
        errors: (Array.isArray(errors) ? errors : [errors]).map((e: any) =>
          t(`common.validation.${e.message || e}`, { defaultValue: e.message || e })
        ),
      })));
    }
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values: Record<string, any>) => {
    setLoading(true);
    try {
      const response = await submitValues(values);

      if (response.success) {
        message.success(t('common.message.save_success'));
        const poId = response.data?.id || id;
        navigate(`/purchase_orders/${poId}`, { replace: true });
      } else {
        applyFieldErrors(response);
        handleApiError(response, {
          t,
          fallbackMessage: t('common.error.save_failed'),
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);

        if (response.success) {
          message.success(t('common.message.save_success'));
          // 重置 antd form + productOptions（ProductSelect 的 defaultOptions）+ 重新套用新建模式預設值
          form.resetFields();
          setProductOptions([]);
          applyNewModeDefaults();
        } else {
          applyFieldErrors(response);
          handleApiError(response, {
            t,
            fallbackMessage: t('common.error.save_failed'),
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // 計算金額摘要
  const watchedItems = Form.useWatch('purchase_order_items_attributes', form) || [];
  const watchedAdjustments = Form.useWatch('purchase_order_adjustments_attributes', form) || [];
  const watchedTaxRate = Form.useWatch('tax_rate', form) || 0;
  const watchedTaxIncluded = Form.useWatch('tax_included', form) || false;

  const amountSummary = useMemo(() => {
    const itemsSubtotal = (watchedItems as Array<Record<string, any>>).reduce((sum, item) => {
      if (!item) return sum;
      return sum + (item.quantity || 0) * (item.unit_price || 0);
    }, 0);

    const adjustmentsTotal = (watchedAdjustments as Array<Record<string, any>>).reduce((sum, adj) => {
      if (!adj || adj._destroy) return sum;
      return sum + (parseFloat(adj.amount) || 0);
    }, 0);

    const subtotal = itemsSubtotal + adjustmentsTotal;

    let taxAmount: number;
    let total: number;
    if (watchedTaxIncluded) {
      taxAmount = watchedTaxRate > 0 ? subtotal - subtotal / (1 + watchedTaxRate) : 0;
      total = subtotal;
    } else {
      taxAmount = subtotal * watchedTaxRate;
      total = subtotal + taxAmount;
    }

    return {
      itemsSubtotal: Math.round(itemsSubtotal * 100) / 100,
      adjustmentsTotal: Math.round(adjustmentsTotal * 100) / 100,
      subtotal: Math.round(subtotal * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  }, [watchedItems, watchedAdjustments, watchedTaxRate, watchedTaxIncluded]);

  const basePath = role === 'operator' ? '/operator' : '/admin';

  // Render-gate: sync 期間（dataLoading 抓 record；merchantDataLoading 切 context 到
  // record 的 merchant）整段 form 不能 mount，避免 supplier / product 下拉先用 header
  // merchant 的清單 render。
  if (dataLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        basePath={basePath}
        items={['purchase_orders', 'purchase_orders.list', {
          label: id
            ? t('shared.purchase_orders.form.title_edit')
            : t('shared.purchase_orders.form.title_new'),
        }]}
      />
      <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          {/* 採購單號 */}
          <Form.Item label={t('shared.purchase_orders.form.name')} required>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="name"
                noStyle
                rules={[{ required: true, message: t('shared.purchase_orders.form.name_required') }]}
              >
                <Input
                  data-testid="po-name-input"
                  placeholder={t('shared.purchase_orders.form.name_placeholder')}
                />
              </Form.Item>
              {!id && (
                <Button
                  onClick={handleSuggestName}
                  loading={suggesting}
                  disabled={loading}
                >
                  {t('shared.purchase_orders.form.suggest_name')}
                </Button>
              )}
            </Space.Compact>
          </Form.Item>

          {/* 供應商 */}
          <Form.Item
            label={t('shared.purchase_orders.form.supplier')}
            name="supplier_id"
            rules={[{ required: true, message: t('shared.purchase_orders.form.supplier_required') }]}
          >
            <Select
              data-testid="po-supplier-select"
              showSearch
              placeholder={t('shared.purchase_orders.form.supplier_placeholder')}
              options={supplierOptions}
              loading={suppliersLoading}
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              onChange={handleSupplierChange}
              notFoundContent={
                <SelectNotFoundContent
                  hint={t('shared.purchase_orders.form.no_suppliers_hint')}
                  settingsPath={`/${role}/suppliers/new`}
                  settingsLabel={t('shared.purchase_orders.form.create_supplier')}
                  onReload={loadSuppliers}
                  reloadLoading={suppliersLoading}
                />
              }
            />
          </Form.Item>

          {/* 幣別 & 稅率 */}
          <Space size="large" style={{ display: 'flex' }} align="start">
            <Form.Item
              label={t('shared.purchase_orders.form.currency')}
              name="currency"
            >
              <Select
                data-testid="po-currency-select"
                showSearch
                allowClear
                placeholder={t('shared.purchase_orders.form.currency_placeholder')}
                options={CURRENCY_OPTIONS}
                style={{ width: 160 }}
              />
            </Form.Item>

            <Form.Item
              label={t('shared.purchase_orders.form.tax_rate')}
              name="tax_rate"
              normalize={(val: number | null | undefined) => val != null ? val / 100 : val}
              getValueProps={(val: number | null | undefined) => ({ value: val != null ? val * 100 : val })}
            >
              <InputNumber
                data-testid="po-tax-rate-input"
                min={0}
                max={100}
                step={1}
                addonAfter="%"
                style={{ width: 160 }}
              />
            </Form.Item>

            <Form.Item
              label={t('shared.purchase_orders.form.tax_included')}
              name="tax_included"
              valuePropName="checked"
            >
              <Checkbox data-testid="po-tax-included-checkbox" />
            </Form.Item>
          </Space>

          {/* 預計交期 */}
          <Form.Item
            label={t('shared.purchase_orders.form.expected_delivery_date')}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="expected_delivery_date" noStyle>
                <DatePicker
                  data-testid="po-delivery-date-picker"
                  style={{ width: '100%' }}
                  placeholder={t('common.placeholder.select_date')}
                />
              </Form.Item>
              <Button
                data-testid="po-delivery-date-today-btn"
                onClick={() => form.setFieldsValue({ expected_delivery_date: dayjs() })}
              >
                {t('common.button.today')}
              </Button>
            </Space.Compact>
          </Form.Item>

          {/* 備註 */}
          <Form.Item
            label={t('shared.purchase_orders.form.note')}
            name="note"
          >
            <TextArea
              rows={3}
              placeholder={t('shared.purchase_orders.form.note_placeholder')}
            />
          </Form.Item>

          {/* 品項明細 */}
          <Divider>{t('shared.purchase_orders.form.items_section')}</Divider>

          <Form.List
            name="purchase_order_items_attributes"
            rules={[
              {
                validator: async (_, items) => {
                  if (!items || items.length === 0) {
                    return Promise.reject(new Error(t('shared.purchase_orders.form.items_required')));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            {(fields, { add, remove }) => {
              addItemRef.current = add as (value: Record<string, unknown>) => void;
              return (
              <div>
                {fields.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ color: '#666' }}>
                      {t('shared.purchase_orders.form.total_items', { count: fields.length })}
                    </span>
                  </div>
                )}

                <Table
                  dataSource={fields.map(field => ({ ...field, fieldKey: field.key }))}
                  rowKey="key"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: t('shared.purchase_orders.form.no_items') }}
                  columns={[
                    {
                      title: t('shared.purchase_orders.form.product'),
                      width: '35%',
                      render: (_: unknown, { name, ...restField }: any) => (
                        <Form.Item
                          {...restField}
                          name={[name, 'product_id']}
                          rules={[{ required: true, message: t('shared.purchase_orders.form.product_required') }]}
                          style={{ marginBottom: 0 }}
                        >
                          <ProductSelect
                            productService={productService}
                            placeholder={t('shared.purchase_orders.form.product_placeholder')}
                            defaultOptions={productOptions}
                            merchantId={recordMerchantId ?? undefined}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      ),
                    },
                    {
                      title: t('shared.purchase_orders.form.quantity'),
                      width: '15%',
                      render: (_: unknown, { name, ...restField }: any) => (
                        <Form.Item
                          {...restField}
                          name={[name, 'quantity']}
                          rules={[{ required: true, message: t('shared.purchase_orders.form.quantity_required') }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber min={1} style={{ width: '100%' }} />
                        </Form.Item>
                      ),
                    },
                    {
                      title: t('shared.purchase_orders.form.unit_price'),
                      width: '15%',
                      render: (_: unknown, { name, ...restField }: any) => (
                        <Form.Item
                          {...restField}
                          name={[name, 'unit_price']}
                          rules={[{ required: true, message: t('shared.purchase_orders.form.unit_price_required') }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                        </Form.Item>
                      ),
                    },
                    {
                      title: t('shared.purchase_orders.form.amount'),
                      width: '15%',
                      render: (_: unknown, { name }: any) => {
                        const itemValues = form.getFieldValue(['purchase_order_items_attributes', name]) || {};
                        const qty = itemValues.quantity || 0;
                        const price = itemValues.unit_price || 0;
                        return <span>{(Math.round(qty * price * 100) / 100).toLocaleString()}</span>;
                      },
                    },
                    {
                      title: t('shared.purchase_orders.form.item_note'),
                      width: '15%',
                      render: (_: unknown, { name, ...restField }: any) => (
                        <Form.Item
                          {...restField}
                          name={[name, 'note']}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder={t('shared.purchase_orders.form.item_note_placeholder')} />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '',
                      width: '5%',
                      render: (_: unknown, { name }: any) => (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          size="small"
                        />
                      ),
                    },
                  ]}
                />

                <Button
                  type="dashed"
                  onClick={() => add({ key: Date.now(), quantity: 1, unit_price: 0 })}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                >
                  {t('shared.purchase_orders.form.add_item')}
                </Button>
              </div>
              );
            }}
          </Form.List>

          {/* 調整項目 */}
          <Divider>{t('shared.purchase_orders.form.adjustments')}</Divider>
          <Form.List name="purchase_order_adjustments_attributes">
            {(fields, { add, remove }) => (
              <div>
                <Table
                  dataSource={fields.map(field => ({ ...field, fieldKey: field.key }))}
                  rowKey="key"
                  pagination={false}
                  size="small"
                  locale={{ emptyText: t('shared.purchase_orders.form.no_adjustments') }}
                  columns={[
                    {
                      title: t('shared.purchase_orders.form.adjustment_description'),
                      width: '55%',
                      render: (_: unknown, { name, ...restField }: any) => (
                        <Form.Item
                          {...restField}
                          name={[name, 'description']}
                          rules={[{ required: true, message: t('common.validation.required') }]}
                          style={{ marginBottom: 0 }}
                        >
                          <Input placeholder={t('shared.purchase_orders.form.adjustment_description')} />
                        </Form.Item>
                      ),
                    },
                    {
                      title: (
                        <span>
                          {t('shared.purchase_orders.form.adjustment_amount')}{' '}
                          <span style={{ color: '#999', fontSize: 12, fontWeight: 'normal' }}>
                            ({t('shared.purchase_orders.form.adjustment_amount_hint')})
                          </span>
                        </span>
                      ),
                      width: '35%',
                      render: (_: unknown, { name, ...restField }: any) => (
                        <Form.Item
                          {...restField}
                          name={[name, 'amount']}
                          rules={[{ required: true, message: t('common.validation.required') }]}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber step={0.01} style={{ width: '100%' }} />
                        </Form.Item>
                      ),
                    },
                    {
                      title: '',
                      width: '10%',
                      render: (_: unknown, { name }: any) => (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          size="small"
                        />
                      ),
                    },
                  ]}
                />
                <Button
                  type="dashed"
                  onClick={() => add({ key: Date.now(), amount: 0 })}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                >
                  {t('shared.purchase_orders.form.add_adjustment')}
                </Button>
              </div>
            )}
          </Form.List>

          {/* 金額摘要 */}
          <Divider>{t('shared.purchase_orders.form.summary')}</Divider>
          <Descriptions bordered column={1} size="small" style={{ maxWidth: 400 }}>
            <Descriptions.Item label={t('shared.purchase_orders.form.items_subtotal')}>
              {amountSummary.itemsSubtotal.toLocaleString()}
            </Descriptions.Item>
            {amountSummary.adjustmentsTotal !== 0 && (
              <Descriptions.Item label={t('shared.purchase_orders.form.adjustments_total')}>
                <span style={{ color: amountSummary.adjustmentsTotal < 0 ? '#cf1322' : '#389e0d' }}>
                  {amountSummary.adjustmentsTotal > 0 ? '+' : ''}{amountSummary.adjustmentsTotal.toLocaleString()}
                </span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('shared.purchase_orders.form.subtotal')}>
              {amountSummary.subtotal.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space size={4}>
                  {t('shared.purchase_orders.form.tax_amount')}
                  <Tooltip
                    overlayInnerStyle={{ maxWidth: 'min(92vw, 440px)', width: 'max-content' }}
                    title={
                      <span style={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                        {t('shared.purchase_orders.form.tax_amount_hint')}
                      </span>
                    }
                  >
                    <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
                  </Tooltip>
                </Space>
              }
            >
              {amountSummary.taxAmount.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label={t('shared.purchase_orders.form.total_amount')}>
              <strong>{amountSummary.total.toLocaleString()}</strong>
            </Descriptions.Item>
          </Descriptions>

          <FloatingActionBarSpacer />
        </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={
          <>
            <Button
              data-testid="po-add-item-floating-btn"
              icon={<PlusOutlined />}
              onClick={handleAddItem}
            >
              {t('shared.purchase_orders.form.add_item')}
            </Button>
            {isNew && (
              <Button
                loading={savingAndAdding}
                disabled={loading}
                onClick={handleSaveAndAddAnother}
              >
                {t('common.button.save_and_add_another')}
              </Button>
            )}
          </>
        }
        submitText={t(isNew ? 'common.button.create' : 'common.button.save')}
      />
    </div>
  );
};

export default PurchaseOrderEdit;
